import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ShoppingBag, Instagram } from "lucide-react";
import { LOGO_WIDE } from "../lib/api";
import { useCart } from "../context/CartContext";

const NAV = [
  { to: "/art", label: "ART" },
  { to: "/prints", label: "STORE" },
  { to: "/murals", label: "MURALS" },
  { to: "/events", label: "EVENTS" },
  { to: "/contact", label: "CONTACT" },
];

export const Header = ({ instagram }) => {
  const [open, setOpen] = useState(false);
  const { count } = useCart();
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 bg-ink/95 backdrop-blur-md border-b border-white/10" data-testid="site-header">
        <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:h-16 sm:py-0">
          <Link to="/" className="flex-1 min-w-0 flex items-center" data-testid="header-logo-link" aria-label="ARTILLERY KDP home">
            <img src={LOGO_WIDE} alt="ARTILLERY KDP" className="h-auto w-[62%] max-w-[300px] sm:w-auto sm:h-12 sm:max-w-none object-contain object-left" draggable={false} />
          </Link>

          <nav className="hidden md:flex items-center gap-7" data-testid="desktop-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `nav-link text-xs font-bold tracking-[0.2em] transition-colors duration-200 hover:text-pink ${isActive ? "text-pink active" : "text-bone"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1">
            <Link
              to="/cart"
              data-testid="header-cart-button"
              aria-label={`Cart, ${count} items`}
              className="relative flex items-center justify-center w-11 h-11 text-bone hover:text-pink transition-colors duration-200"
            >
              <ShoppingBag size={20} strokeWidth={1.75} />
              {count > 0 && (
                <span data-testid="header-cart-count" className="absolute top-1 right-0 bg-pink text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                  {count}
                </span>
              )}
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="menu-open-button"
              aria-label="Open menu"
              className="md:hidden flex items-center justify-center w-11 h-11 text-bone"
            >
              <Menu size={24} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[80] bg-ink flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <img src={LOGO_WIDE} alt="ARTILLERY KDP" className="h-auto w-[55%] max-w-[260px] object-contain object-left" draggable={false} />
              <button onClick={() => setOpen(false)} data-testid="menu-close-button" aria-label="Close menu" className="w-11 h-11 flex items-center justify-center">
                <X size={26} strokeWidth={1.75} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-6 gap-1">
              {[{ to: "/", label: "HOME" }, ...NAV].map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.4 }}
                >
                  <NavLink
                    to={n.to}
                    data-testid={`menu-${n.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block py-2 font-display text-5xl tracking-wide leading-none transition-colors duration-200 ${isActive ? "text-pink" : "text-bone"}`
                    }
                  >
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-6 pb-10 flex items-center justify-between border-t border-white/10 pt-5">
              <a
                href={instagram || "https://instagram.com/swingkidd"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="menu-instagram-link"
                className="flex items-center gap-2 text-sm text-smoke min-h-[44px]"
              >
                <Instagram size={18} /> @swingkidd
              </a>
              <span className="text-[10px] tracking-[0.25em] text-smoke">EST. KENNY DEPRINCE</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
